/// enum len pro barevnou variantu loga - ne motivu
export enum LogoColorVariant {
    Original = 'original',
    Black = 'black',
    White = 'white',
    Custom = 'custom'
}

/// enum len pro barevnou variantu motivu - ne loga
export enum MotiveColorVariant {
    Original = 'original',
    Black = 'black',
    White = 'white'
}


export interface ITronEditorParams {
    /**
     * ID divu kde se zobrazuje editor
     */
    editorDivId: string; // ok

    /**
     * ID divu kde se zobrazuje visualizace
     */
    visualizationDivId: string; // ok

    /**
     * nastaveni pro watermark (pokud je aktivni)
     */
    watermarkSettings?: IWatermarkSettings;

    /**
     * fce, ktera nacte obrazek produktu s konkretnim Guid (Content) loadLogoImage(logoId: string): string // vraci base 64 content image
     */
    loadLogoImage(logoId: string, type?: LogoColorVariant): Promise<string>; // returns promise of logo image as base64

    /**
     * callback fce pokud se změní select prvku
     */
    selectedCallback: (index: number | undefined) => void;

    /**
     * callback na udalost zmeny velikosti printArea
     */
    printZoneSizesChangedCallback: () => void;

    fontSizeCallback: (fontSize: number) => void; // retuns font size of selected text shape

    initEditorCallback: () => void; // vyvola callback po nahrani editoru a jeho parametry, ma to byt pro signalizaci vypnut animovany gif loadingu

    visualizationCallback: () => void; // vyvola callback po renderingu visualizace

    getPantoneFromCOLOR(value:string[]): Promise<string>;
    getHexFromPantone(value: string[]): Promise<string>;
}

/**
 * Print Position and technology
 */
export interface IPrintData {
    motiveWidth: number;
    motiveHeight: number;
    posCode: string;
    posName: string;
    posDefault: boolean;
    techCode: string;
    techName: string;
    techDefault: boolean;
    areaWidth: number;
    areaHeight: number;
    maxColors: string;
}

export interface ISize {
    width: number;
    height: number;
}

export interface IVisualizationModeParam {
    printType:PrintEffectType; // typ potisku
    shadowAmount:number; // sila efektu stinu
    shadowOffsetX:number; // posun efektu v ose x pro stin
    shadowOffsetY:number; // posun effektu v ose y pro stin
    shadowBlur:number; // sila blur efektu pro stin
    shadowColor?:string; // barva stinu
    lightAmount:number; // sila efektu svetla
    lightOffsetX:number; // posun efektu v ose x pro svetlo
    lightOffsetY:number; // posun effektu v ose y pro svetlo
    lightBlur:number; // sila blur efektu pro svetlo
    lightColor?:string; // barva svetla
    isWood:boolean; // jedna se o drevo
    surfaceDetail:number; // detail povrchu materialu
    surfaceColor?:string; // barva stribrna, zlata, produktova barva
    maskColorWood?:string; // barevna "gray-scale" maska pro vypocet odstinu na efekt drevo laser
    maskColorEmboss?:string; // barevna "gray-scale" maska pro vypocet odstinu na efekt drevo emboss
}

export interface ITronEditor {
    visualObjects: IVisualObject[]; // vrací seznam všech prvků v editoru
    createShape(type: ShapeType): Promise<number>; // zalozi objekt (typu text/rect/line) a vrátí index nového prvku
    createImage(logoId: string, type?: LogoColorVariant): Promise<number>; // zalozi objekt typu image(logo) a vrátí index nového prvku
    setShapeProps(index: number, data: any): void; // nastavuje vlastnosti jednoho prvku
    sendToBack(index: number): void; // posunutí prvku úplně dozadu
    bringToFront(index: number): void; // posunutí prvku úplně dopředu
    deleteShape(index: number): void; // odstranění prvku - po něm editor sám vyvolá SelectedCallback a tím se přeenastaví na následující existující prvek
    magnetEnabled: boolean; // nastavi nebo vypne fci magnetu
    getCanvasColors():Promise<string[]>; // vrátí seznam TOP 10 barev (seřazené dle % plochy od největší) ve formě pole stringu "rgba(rrr, ggg, bbb, a.a)"

    undo(): void;
    redo(): void;

    initEditor(): Promise<void>;
    initWatermark(watermarkLogoBase64?:string):Promise<void>;
    initProduct(productImageBase64: string, visualizationXML: string, printZoneColors:string): Promise<void>;
    initMotiveData(motiveDataJson: string, motive:MotiveColorVariant): Promise<void>; // setting complete motive
    initMotiveFromLogo(logoId: string, motive: LogoColorVariant): Promise<void>; // setting plain logo
    getMotiveData(): Promise<string>; // returns motive data - JSON
    getPrintData(): IPrintData;
    getMotiveBitmap(): Promise<string[]>; // returns motive raster array [original, black, white]

    setEditorSize(size: ISize): void; // size of editor in px
    setVisualizationSize(size: ISize): void; // size of visualization in px
    setEditorBackground(value:string): void; // nastavi farbu pozadia editora
    setShapeBorderColor(color:string): void;
    setVisualizationModeEffect(param:IVisualizationModeParam): void; // Laser visualizace
    setVisualizationBlendingMode(mode:BlendingMode): void; // Blending mod "Light kombinace"
}

export enum ShapeType {
    Rect = 'rectangle',
    Text = 'text',
    Line = 'line',
    Logo = 'image'
}

export interface IVisualObject {
    ShapeType: ShapeType; // enum Rect, Text, Line, Logo
}

export interface IRect extends IVisualObject {
    StrokeWidth: number;
    StrokeColor: string;
    FillColor: string;
}

export interface IText extends IVisualObject {
    StrokeWidth: number;
    StrokeColor: string;
    FillColor: string;
    Text: string;
    FontFamily: string;
    Align: string;
    Bold: boolean;
    Italic: boolean;
    FontSize: number; // read-only font size of text shape, change signaled by fontSizeCallback
}

export interface ILine extends IVisualObject {
    StrokeWidth: number;
    StrokeColor: string;
}

export interface ILogo extends IVisualObject {
    LogoId: string; // id obrázku loga ze systému TronCloud (načítá se fcemi které jsou v předchozích interfacech)
    variant: LogoColorVariant; // barevna varianta loga
    customColor?: string; // (volitelna) promena specifikujici barvu loga v HEX pro variantu 'custom'
}

// typ gravirovania na produkty
export enum LaserBevelType
{
    None,
    Shadow,
    Light,
}
export enum PrintEffectType
{
    None = "None",
    Laser = "Laser", // laser
    Emboss = "Emboss", // razba
    Embroidery = "Embroidery", // vysivka
    Standard = "Standard" // bez effektu
}

export enum BlendingMode {
    None = "None",
    Darken = "Darken",
    Multiply = "Multiply",
    ColorBurn = "ColorBurn",
    LinearBurn = "LinearBurn",
    Lighten = "Lighten",
    Screen = "Screen",
    ColorDodge = "ColorDodge",
    LinearDodge = "LinearDodge",
    Overlay = "Overlay",
    SoftLight = "SoftLight",
    HardLight = "HardLight",
    VividLight = "VividLight",
    LinearLight = "LinearLight",
    PinLight = "PinLight",
    Difference = "Difference",
    Exclusion = "Exclusion",
    SoftLightLighten = "SoftLightLighten",
    Laser = "Laser",
    Shadows = "Shadows",
    Emboss = "Emboss",
    EmbossFoil = "EmbossFoil"
}

export interface IWatermarkSettings {
    on: boolean;
    mode: "Text" | "Image";
    opacity: number;
    text: string;
    fontFamily: string;
    fontColor: string;
    angle: number;
    style: "Single" | "Mosaic";
    mosaicNumber:number;
}



// WEBPACK FOOTER //
// ./src/App/logo-app/logo-editor/tron-editor-api.tsx